import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { FaqBlock, ResultCard, ReservedResultsArea, SubHubGrid, buildBreadcrumbLd } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, useAnalyzeGate, todayISO } from "@/components/age-calculator/premium";
import { AnchorToc, AnchorSection, buildAppFaqLd, chronoDiff, CopyResultButton } from "@/components/age-calculator/sub-shell";

const TITLE = "Pet Age Converter: Dog & Cat Age in Human Years (AVMA Formula)";
const DESC = "Convert your dog or cat's age to accurate equivalent human years using the AVMA logarithmic formula, size-based Purina model and the classic ×7 rule side by side.";

const FAQS = [
  { q: "How do you convert dog years to human years accurately?", a: "AVMA (American Veterinary Medical Association) uses: human age = 16 × ln(dog age) + 31. This beats the outdated ×7 rule for dogs older than 2 years." },
  { q: "How is cat age converted to human years?", a: "The standard model: year 1 = 15 human years, year 2 adds 9 more (=24 human), and every year after adds 4 human years." },
  { q: "Does breed size change the conversion?", a: "Yes — large-breed dogs age faster. A 10-year-old giant breed is roughly 66 in human years, while a small breed the same age is closer to 56." },
  { q: "Is the ×7 rule wrong?", a: "It's a rough approximation only. Dogs mature much faster in their first 2 years, so ×7 undercounts puppyhood and overcounts old age." },
];

const TOC = [
  { id: "calculator", label: "Calculator" },
  { id: "formulas", label: "Formulas Compared" },
  { id: "stages", label: "Life Stages" },
  { id: "faqs", label: "FAQs" },
];

export const Route = createFileRoute("/age-calculator/pet-age-converter")({
  head: () => leafHead({
    path: "/age-calculator/pet-age-converter", title: TITLE, description: DESC,
    scripts: [
      buildAppFaqLd({ name: "Pet Age Converter", description: DESC, faqs: FAQS }),
      buildBreadcrumbLd([
        { name: "Home", url: "https://drhint.com/" },
        { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        { name: "Pet Age Converter", url: "https://drhint.com/age-calculator/pet-age-converter" },
      ]),
    ],
  }),
  component: Page,
});

type Species = "dog" | "cat";
type Size = "small" | "medium" | "large" | "giant";

function dogHumanAge(y: number, size: Size) {
  if (y <= 0) return 0;
  if (y <= 2) return y === 1 ? 15 : 24;
  // AVMA logarithmic
  const base = 16 * Math.log(y) + 31;
  const sizeAdj = { small: -3, medium: 0, large: 3, giant: 6 }[size];
  return Math.max(0, base + sizeAdj);
}
function catHumanAge(y: number) {
  if (y <= 0) return 0;
  if (y === 1) return 15;
  if (y === 2) return 24;
  return 24 + (y - 2) * 4;
}

function Page() {
  const [dob, setDob] = useState("");
  const [species, setSpecies] = useState<Species>("dog");
  const [size, setSize] = useState<Size>("medium");
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const r = useMemo(() => (dob ? chronoDiff(dob, todayISO()) : null), [dob]);
  const petYears = r ? r.years + r.months / 12 : 0;
  const human = species === "dog" ? dogHumanAge(petYears, size) : catHumanAge(petYears);
  const timesSeven = petYears * 7;
  const copyText = r ? `${species === "dog" ? "🐕" : "🐈"} Pet age ${petYears.toFixed(1)} yrs → ${human.toFixed(0)} human yrs (${species === "dog" ? `AVMA + ${size} breed` : "Cat model"})` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Pet Age Converter
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          <strong>Dog</strong> ya <strong>cat</strong> ki DOB daalein — hum AVMA logarithmic model use karke actual equivalent human years dikhate hain, plus classic ×7 comparison. Deeper species-specific tool <Link to="/age-calculator/pet-dog-cat" className="text-primary underline">pet age hub</Link> par.
        </p>
      </section>
      <AnchorToc items={TOC} />

      <section id="calculator" className="mx-auto max-w-5xl px-4 mt-4 scroll-mt-24">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <TripleDateInput label="Pet's Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} max={todayISO()} />
            <div>
              <div className="text-sm font-semibold mb-2">Species</div>
              <div className="flex gap-2 mb-4">
                {(["dog", "cat"] as Species[]).map((s) => (
                  <button key={s} type="button" onClick={() => { setSpecies(s); reset(); }} className={`px-4 py-1.5 rounded-full text-xs font-bold transition ${species === s ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}>{s === "dog" ? "🐕 Dog" : "🐈 Cat"}</button>
                ))}
              </div>
              {species === "dog" && (
                <>
                  <div className="text-sm font-semibold mb-2">Breed Size</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(["small", "medium", "large", "giant"] as Size[]).map((s) => (
                      <button key={s} type="button" onClick={() => { setSize(s); reset(); }} className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${size === s ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <AnalyzeButton onClick={trigger} disabled={!dob || analyzing} label="Convert Pet Age" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={260}>
            {revealed && r ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Actual Pet Age" value={`${r.years}y ${r.months}m`} sub={`${petYears.toFixed(1)} yr decimal`} />
                  <ResultCard label="Human Equivalent" value={`${human.toFixed(0)} yrs`} sub={species === "dog" ? `AVMA + ${size}` : "Cat model"} />
                  <ResultCard label="Classic ×7 Rule" value={`${timesSeven.toFixed(0)} yrs`} sub="Legacy formula" />
                  <ResultCard label="Delta vs ×7" value={`${(human - timesSeven).toFixed(1)}`} sub={human > timesSeven ? "Modern > legacy" : "Modern < legacy"} />
                </div>
                <div className="mt-4 flex justify-center"><CopyResultButton text={copyText} label="Copy Result" /></div>
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter pet DOB, choose species and size.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <AnchorSection id="formulas" title="Three Formulas Compared">
        <ul>
          <li><strong>×7 Rule (Legacy):</strong> simple but inaccurate — undercounts fast puppy growth.</li>
          <li><strong>AVMA Logarithmic (2019+):</strong> <code>16 × ln(dog_age) + 31</code> — matches DNA-methylation studies.</li>
          <li><strong>Purina Size Model:</strong> adjusts by breed size — giant breeds age +3 human years faster per pet year post age 5.</li>
        </ul>
      </AnchorSection>

      <AnchorSection id="stages" title="Life Stages Quick Reference">
        <table>
          <thead><tr><th>Pet Age</th><th>Dog (Medium)</th><th>Cat</th></tr></thead>
          <tbody>
            <tr><td>1 yr</td><td>15</td><td>15</td></tr>
            <tr><td>2 yr</td><td>24</td><td>24</td></tr>
            <tr><td>5 yr</td><td>~57</td><td>36</td></tr>
            <tr><td>10 yr</td><td>~68</td><td>56</td></tr>
            <tr><td>15 yr</td><td>~74</td><td>76</td></tr>
          </tbody>
        </table>
      </AnchorSection>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />
    </Layout>
  );
}